import { CPCompare } from "../utils/CPCompare";
import { Div2Floor } from "../utils/Div2Floor";
import { CenterIt } from "../utils/CenterIt.js";
import { QColorPickerResult } from "./QColorPickerResult.js";
import { QOptionArray } from "./QOptionArray.js";
import {
  enumBoxHeightMode,
  enumBoxSubTypes,
  enumBoxWidthMode,
  enumCalcCode,
  enumCellContentType,
  enumCellRefType,
  enumChartBoxInteractionMode,
  enumChartElement,
  enumDividerDirection,
  enumKeyboardTargetEnterAction,
  enumMarqueeBehavior,
  enumSliderDirection,
  enumUpdateProperty,
  kDefaultCellHeight,
  kFramelessHeaderHeight,
  kMaxBoxSize,
  kReturnKey,
  kTriangleWidth
} from "../constants";
import { QBasePanel } from "./QBasePanel";
import { QChartBox } from "./QChartBox";
import { QColorPicker } from "./QColorPicker";
import { QDivider } from "./QDivider";
import { QDropDown } from "./QDropDown";
import { QLayoutDragPanel } from "./QLayoutDragPanel";
import { QSlider } from "./QSlider";

export class QLayoutPanel extends QBasePanel {
  constructor() {
    super();
    this.fLayout = null;
    this.fBoxMode = enumChartBoxInteractionMode.layout;
    this.fColumnResizer = null;
    this.fMinMaxResizer = null;
    this.fWSMgr = null;
    this.fDraggerPanel = null;
    this.fChartBox = null;
    this.fPreviewMode = false;
    this.fPreviewDBObj = null;
    this.fShowSizers = true;
    this.fEditBoxStyle = false;
    this.fMarqueeSelectNestedElements = true;
    this.fCurCursor = "default";
    this.fIsHoverHighlightEnabled = false;
    this.fHasControlRibbon = false;
    this.fUseResizers = true;
    this.fRenderPhoenix = true;
    this.fNoHighlight = true;
    this.fNameChange = false;
  }

  InitLayoutPanel(backgroundPanel, theLayout, wsMgr, previewDBObj, editBoxStyle, doOverrides) {
    this.fEditBoxStyle = editBoxStyle;
    this.fPreviewDBObj = previewDBObj;
    this.fLayout = theLayout;
    this.fWSMgr = wsMgr;
    this.fParent = backgroundPanel;
    this.fUIColors = wsMgr.GetUIColors();
    this.fSaveOverrides = doOverrides;

    this.InitBasePanel(backgroundPanel);
    this.SetPanelClickCmd("LayoutClick");
    this.SetPanelDblClickCmd("LayoutDblClick");
    this.SetMinMaxZoom(1.0, 4.0);
    this.SetScaleFactor(1.0); // 1.5

    this.SetMarqueeEnabled(true);
    this.SetMarqueeBehavior(enumMarqueeBehavior.partialOverlap);

    this.Resize();
    this.PositionAltBackground();
    this.SetPanelToolMode("LayoutToolMode1");
    wsMgr.SetKeyboardTarget(null, this, enumKeyboardTargetEnterAction.noAction);

    this.SetBorderColor(this.GetFillColor());
    this.SetModalOn(true);
  }

  IsHoverHighlightEnabled() {
    return false;
  }

  //=================================================================================================
  // GetCursor
  GetCursor() {
    return this.fCurCursor;
  }

  //=================================================================================================
  // GetCursor
  EditBoxStyle() {
    return this.fEditBoxStyle;
  }

  //=================================================================================================
  // Preview mode handling
  GetPreviewMode() {
    return this.fPreviewMode;
  }

  SetPreviewPerson(dbObj) {
    this.fPreviewDBObj = dbObj;
    this.fPreviewMode = !!dbObj;
    this.BuildLayout(this.fLayout, false);
  }

  IsStdBox() {
    var boxSubType = this.fLayout.GetBoxSubType();
    var nonStandardBoxTypes = [
      enumBoxSubTypes.textBox,
      enumBoxSubTypes.picture,
      enumBoxSubTypes.enumeratedTable
    ];
    return !nonStandardBoxTypes.includes(boxSubType);
  }

  IncludeElementInSelection(theElement) {
    var elementType = theElement.GetElementType();
    if (elementType == enumChartElement.boxCell) return true;
    else return false;
  }

  //=================================================================================================
  GetLayout() {
    return this.fLayout;
  }

  GetNameChange() {
    return this.fNameChange;
  }

  PositionMinMaxControls() {
    var theLayout = this.fLayout;
    var sizingMode = theLayout.GetWidthSizingMode();
    if (sizingMode == enumBoxWidthMode.fixedwidth) return;
    var minWidth = theLayout.GetWidthMin();
    var maxWidth = theLayout.GetWidthMax();
    if (minWidth <= 0) console.log("MinWidth Not Set");
    if (maxWidth <= 0) console.log("MaxWidth Not Set");
    var minSize = theLayout.GetMinWidthForBump();
    this.fMinMaxResizer.SetMinValue(minSize);
    this.fMinMaxResizer.SetKnobPosition(0, minWidth);
    this.fMinMaxResizer.SetKnobPosition(1, maxWidth);
  }

  UpdateBoxSizingMode(rebuildLayout) {
    var theLayout = this.fLayout;
    var sizingMode = theLayout.GetWidthSizingMode();

    var showMinMax = false;
    var showFixed = true;
    if (sizingMode != enumBoxWidthMode.fixedwidth) {
      showMinMax = true;
      showFixed = false;
      this.PositionMinMaxControls();
    }

    if (!this.GetShowResizers()) {
      showFixed = false;
      showMinMax = false;
    }

    this.fColumnResizer.SetShownState(showFixed);
    this.fMinMaxResizer.SetShownState(showMinMax);

    if (rebuildLayout) this.BuildLayout(this.fLayout, true);
  }

  AddResizers(theLayout, xPos, yPos) {
    var numCols = theLayout.GetNumColWidths();
    //var theLayout = this.fLayout;

    var maxBoxSize = kMaxBoxSize;
    if (
      theLayout.GetBoxSubType() === enumBoxSubTypes.enumeratedTable &&
      theLayout.GetAggregatedFieldRef() != null
    ) {
      if (theLayout.GetWidthMax() > maxBoxSize) maxBoxSize = theLayout.GetWidthMax();
    }
    var sx = xPos;
    var sy = yPos + theLayout.GetBoxHeight(false) + 10;
    this.fColumnResizer = new QSlider();
    this.fColumnResizer.InitSlider(sx, sy, maxBoxSize, null);

    var minVal;

    if (numCols == 3) minVal = theLayout.GetMinWidthForBump();
    else minVal = theLayout.GetMinWidthForBumpFirstCol();

    minVal -= kTriangleWidth / 2 + 2;
    this.fColumnResizer.SetMinValue(minVal);

    this.fColumnResizer.SetCmdCode("ResizeColumn");

    var ry = yPos - 10;
    this.fMinMaxResizer = new QSlider();
    this.fMinMaxResizer.InitSlider(sx, ry, maxBoxSize, null); //+40
    this.fMinMaxResizer.SetSliderColor("#0000FF");
    this.fMinMaxResizer.SetSliderDirection(enumSliderDirection.pointsDown);
    this.fMinMaxResizer.SetCmdCode("ChgMinMax");

    var minWidth = theLayout.GetWidthMin();
    var maxWidth = theLayout.GetWidthMax();
    if (minWidth == -1) minWidth = 0;
    if (maxWidth == -1) maxWidth = 0;

    var knobPos = theLayout.GetColWidth(1) + theLayout.GetColWidth(0) - 1;
    for (var i = 2; i < numCols; i++) {
      this.fColumnResizer.AddSliderKnob(knobPos);
      knobPos = knobPos + theLayout.GetColWidth(i) - 1;
    }

    this.fMinMaxResizer.AddSliderKnob(minWidth);
    this.fMinMaxResizer.SetSliderLabel("Min", "right", 0);

    this.fMinMaxResizer.AddSliderKnob(maxWidth);
    this.fMinMaxResizer.SetSliderLabel("Max", "left", 1);

    this.PushElement(this.fMinMaxResizer);
    this.PushElement(this.fColumnResizer);
    this.UpdateBoxSizingMode(false);

    this.PanelToolModeChanged();
  }

  HandleAutoHeight(theLayout) {
    var flag = null;
    var sizingMode = theLayout.GetHeightSizingMode();
    if (sizingMode == enumBoxHeightMode.autosmart) theLayout.SetSmartHeight(-1);
  }

  HandleAutoWidth(theLayout) {
    var flag = null;
    var sizingMode = theLayout.GetWidthSizingMode();
    if (sizingMode == enumBoxWidthMode.autosmart) {
      flag = theLayout.GetUseCalcWidth();
      theLayout.SetUseCalcWidth(true);
    }

    return flag;
  }

  BuildLayout(theLayout, recalcLayout) {
    if (this.fChartBox) {
      this.fChartBox.SaveCellSelection(theLayout);
      this.ClearSelection();
      // Whenever we rebuild assume that the content may force resizing of the box
      // For example, text wrapping and preview mode
      if (recalcLayout) {
        var flagW = this.HandleAutoWidth(theLayout);
        this.HandleAutoHeight(theLayout);
        theLayout.CalculateLayout();
        if (flagW != null) theLayout.SetUseCalcWidth(flagW);
      }
    }

    this.fAltBackground = null;
    this.RemoveAllElements();

    var boxHeight = theLayout.GetBoxHeight(false);
    var availableX = this.GetWidth();

    var yStart = this.fHasControlRibbon ? kFramelessHeaderHeight * 4 : 0; // control panel top ribbon
    var availableY = Div2Floor(this.GetPortHeight());
    var yPos = CenterIt(availableY, boxHeight);

    var xPos = CenterIt(availableX, theLayout.GetBoxWidth());

    if (yPos < yStart) yPos = yStart;

    this.fChartBox = new QChartBox();
    this.HandleAutoHeight(theLayout); // HandleAutoHeight paired with GetBoxHeight results in CalcBoxHeight == GetBoxHeight. This specific line is intended to pair with an InitChartBox call that calls GetBoxHeight.
    this.fChartBox.InitChartBox(
      theLayout,
      this,
      xPos,
      yPos,
      this.fBoxMode,
      this.fPreviewDBObj,
      null,
      true,
      this.fWSMgr,
      false,
      true // suppressOverlays - we don't want overlays in the layout editor
    );
    this.fChartBox.SetBoxBorderColor(this.fLayout.GetBoxBorderColor());
    this.fChartBox.SetDraggable(false);
    this.fChartBox.GetBoxMode = function () {
      var myLayout = this.fLayout;
      if (
        myLayout.GetBoxSubType() === enumBoxSubTypes.enumeratedTable &&
        myLayout.GetAggregatedFieldRef() == null
      )
        return enumChartBoxInteractionMode.readOnly;
      return this.fBoxMode;
    };
    this.fChartBox.fIsHighlighted = false;
    //this.DoSelectElement(this.fChartBox, true);
    if (this.fUseResizers) this.AddResizers(theLayout, xPos, yPos);
  }

  PanelToolModeChanged() {
    var toolMode = this.GetPanelToolMode();
    this.fShowSizers = true;

    switch (toolMode) {
      case "LayoutToolMode1":
        this.fBoxMode = enumChartBoxInteractionMode.layout;
        this.fCurCursor = "default";
        break;
      case "LayoutToolMode2":
        this.fBoxMode = enumChartBoxInteractionMode.marquee;
        this.fCurCursor = "crosshair";
        break;
      case "LayoutToolMode3": // Pan
        this.fShowSizers = false;
        this.fCurCursor = "pointer";
        break;
    }

    this.fChartBox.SetBoxMode(this.fBoxMode);
    this.UpdateBoxSizingMode(false);
  }

  GetShowResizers() {
    var myLayout = this.fLayout;
    if (
      myLayout.GetBoxSubType() === enumBoxSubTypes.enumeratedTable &&
      myLayout.GetAggregatedFieldRef() == null
    )
      return false;
    return this.fShowSizers;
  }

  Resize(origParentWidth, origParentHeight, newParentWidth, newParentHeight) {
    super.Resize(origParentWidth, origParentHeight, newParentWidth, newParentHeight);
    var scaleFactor = this.GetScaleFactor();
    if (scaleFactor <= this.GetMinZoom()) {
      this.SetPanX(0);
      this.SetPanY(0);
    } else {
      this.PanPanel(this.GetPanX(), this.GetPanY(), 0, 0);
    }
  }

  ResizeCanvasCenter() {
    this.Resize();
  }

  PostStandardResize() {
    var bsMgr = this.fWSMgr.fBoxStylesMgr;
    if (bsMgr && this.fLayout != null) {
      var curSet = bsMgr.GetBoxStyleSetFromID(this.fLayout.fBoxStyleID, true);
      bsMgr.SwitchBoxStyles(curSet);
      localStorage.boxstyleset = curSet; // not ideal to set this here, but on resize, the tabs are rebuilt and the last tab created always gets saved to local storage
    } else {
      var canvasContainer = document.getElementById("canvasContainer");
      var canvasRect = canvasContainer.getBoundingClientRect();
      this.SizePanel(canvasRect.width, canvasRect.height);
    }
  }

  DoClosePanel() {
    var w = this.fWSMgr;
    this.fAltBackground = null;
    this.ClearSelection();
    w.CloseLayoutHoverToolbar();
  }

  DoCustomResize() {
    var canvasContainer = document.getElementById("canvasContainer");
    var canvasRect = canvasContainer.getBoundingClientRect();
    this.SizePanel(canvasRect.width, canvasRect.height);
    this.BuildLayout(this.fLayout, false);
    this.PositionAltBackground();
  }

  DoHandleRightClick(hitElement, x, y, event) {
    if (!hitElement) {
      return;
    }

    var isCell = hitElement.GetElementType() === enumChartElement.boxCell;
    var elementType = hitElement.GetElementType();

    if (isCell && !hitElement.IsSelected()) {
      this.ClearSelection();
      this.DoSelectElement(hitElement, true);
    }

    if (elementType === enumChartElement.boxCell) {
      this.fWSMgr.fContextMenuHandler?.("cell", event, {
        box: hitElement.GetParentBox(),
        cell: hitElement
      });
    }
  }

  GetDeleteColumns() {
    var chartBox = this.fChartBox;
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetDeleteColumns(this.fLayout, boxCells);
  }

  GetDeleteRows() {
    var chartBox = this.fChartBox;
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetDeleteRows(this.fLayout, boxCells);
  }

  GetChartBox() {
    return this.fChartBox;
  }

  GetOuterSelectionColumn(leftMost) {
    var chartBox = this.fChartBox;
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetOuterSelectionColumn(this.fLayout, boxCells, leftMost);
  }

  GetOuterSelectionRow(topMost) {
    var chartBox = this.fChartBox;
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetOuterSelectionRow(this.fLayout, boxCells, topMost);
  }

  DoColInsert(insertLeft) {
    this.ApplyColumnInsert(this.fLayout, insertLeft);
    this.BuildLayout(this.fLayout, true);
    this.fWSMgr?.OnLayoutMutated?.();
  }

  DoRowInsert(insertAbove) {
    this.ApplyRowInsert(this.fLayout, insertAbove);
    this.BuildLayout(this.fLayout, true);
    this.fWSMgr?.OnLayoutMutated?.();
  }

  SelectCell(colIndex, rowIndex) {
    this.ClearSelection();
    var chartBox = this.fChartBox;
    var layoutCells = chartBox.fLayoutCells;
    return this.DoSelectCell(layoutCells, colIndex, rowIndex);
  }

  PerformSelect(theElement, selectIt) {
    var w = this.fWSMgr;
    var keyAction = enumKeyboardTargetEnterAction.noAction;

    theElement.SetSelected(selectIt);
    if (!selectIt && theElement.IsSelected()) {
      this.fWSMgr.SetKeyboardTarget(null, this, keyAction);
    } else {
    }
    this.fWSMgr.SetKeyboardTarget(theElement, this, keyAction);

    w.UpdateLayoutHoverToolbar(theElement, this.fChartBox);
  }

  DoRowDelete() {
    var postSelectRow = this.ApplyRowDelete(this.fLayout);
    this.BuildLayout(this.fLayout, true);
    this.SelectCell(1, postSelectRow);
    this.fWSMgr?.OnLayoutMutated?.();
  }

  DoColumnDelete() {
    var postSelectCol = this.ApplyColumnDelete(this.fLayout);
    this.BuildLayout(this.fLayout, true);
    this.SelectCell(postSelectCol, 1);
    this.fWSMgr?.OnLayoutMutated?.();
  }

  ManageRowsAndColumns(theCode) {
    switch (theCode) {
      case "DeleteRows":
        this.DoRowDelete();
        break;
      case "DeleteColumns":
        this.DoColumnDelete();
        break;
      case "InsertRowAbove":
        this.DoRowInsert(true);
        break;
      case "InsertRowBelow":
        this.DoRowInsert(false);
        break;
      case "InsertColumnLeft":
        this.DoColInsert(true);
        break;
      case "InsertColumnRight":
        this.DoColInsert(false);
        break;
      case "MergeCells":
        this.DoMergeCells();
        break;
      case "UnmergeCells":
        this.DoUnmergeCells();
        break;
    }
  }

  UpdateSelectedProperty(parameter, theProperty) {
    var theLayout = this.fLayout;
    var [handled, reBuildLayout] = theLayout.UpdateOverride(
      parameter,
      theProperty,
      this.fSaveOverrides
    );

    if (!handled) {
      var chartBox = this.fChartBox;
      var boxCells = chartBox.fLayoutCells;

      var numElements = boxCells.length;

      for (var i = 0; i < numElements; i++) {
        var theElement = boxCells[i];
        var cellDesc = theElement.GetCellDesc();
        if (theElement.IsSelected() && !cellDesc.SkipGutterCell(theProperty))
          reBuildLayout = cellDesc.UpdateBoxCellDesc(parameter, theProperty, this.fSaveOverrides);
      }
    }

    if (reBuildLayout) this.BuildLayout(this.fLayout, true); // Changing the property changes the size of cells...
  }

  SurveySelectedProperty(theProperty) {
    var boxProperty = true;
    var theLayout = this.fLayout;
    var parameter = null;

    switch (theProperty) {
      case enumUpdateProperty.boxFillColor:
        var cpResult = new QColorPickerResult();
        cpResult.fFillMode = theLayout.GetBoxFillMode();
        cpResult.fSelectedColor = theLayout.GetBoxFillColor();
        cpResult.fSelectedColor2 = theLayout.GetBoxFillColorGradient();
        parameter = cpResult;
        break;
      case enumUpdateProperty.boxBorderColor:
        var cpResult = new QColorPickerResult();
        cpResult.fSelectedColor = theLayout.GetBoxBorderColor();
        parameter = cpResult;
        break;
      case enumUpdateProperty.boxShapeType:
        parameter = theLayout.GetBoxShapeType();
        break;
      case enumUpdateProperty.boxShadow:
        parameter = theLayout.GetShadowOn();
        break;
      case enumUpdateProperty.boxBorderWeight:
        parameter = theLayout.GetBorderThickness();
        break;
      case enumUpdateProperty.boxBorderStyle:
        parameter = theLayout.GetBorderStyle();
        break;
      case enumUpdateProperty.boxDashStyle:
        parameter = theLayout.GetDashStyle();
        break;
      case enumUpdateProperty.boxFlag:
        parameter = {
          fFlagType: theLayout.GetFlagType(),
          fFlagColor1: theLayout.GetFlagColor1(),
          fFlagColor2: theLayout.GetFlagColor2(),
          fFlagColor3: theLayout.GetFlagColor3(),
          fFlagLabel: theLayout.GetFlagLabel(),
          fTriPosition: theLayout.GetTriPosition()
        };
        break;
      default:
        boxProperty = false;
        break;
    }

    if (boxProperty) return parameter;
    var chartBox = this.fChartBox;
    var boxCells = chartBox.fLayoutCells;

    var numElements = boxCells.length;
    var prevParameter = null;
    var altCompare = false;
    var altCompare2 = false;

    for (var i = 0; i < numElements; i++) {
      //var skipComparison = false;
      var theElement = boxCells[i];
      var elementType = theElement.GetElementType();

      if (theElement.IsSelected() && elementType != -1) {
        var cellDesc;
        cellDesc = theElement.GetCellDesc();

        if (!cellDesc.SkipGutterCell(theProperty)) {
          switch (theProperty) {
            case enumUpdateProperty.cellTextItalic:
              parameter = cellDesc.GetTextItalic();
              break;
            case enumUpdateProperty.cellTextBold:
              parameter = cellDesc.GetTextBold();
              break;
            case enumUpdateProperty.cellExpandRow:
              parameter = cellDesc.GetExpandRow();
              break;
            case enumUpdateProperty.cellRefType:
              parameter = cellDesc.GetCellRefType();
              break;
            case enumUpdateProperty.cellTextUnderline:
              parameter = cellDesc.GetTextUnderline();
              break;
            case enumUpdateProperty.cellFillColor:
              var cpResult = new QColorPickerResult();
              cpResult.fSelectedColor = cellDesc.GetCellFillColor();
              cpResult.fSelectedColor2 = cellDesc.GetCellFillColor2();
              cpResult.fFillMode = cellDesc.GetCellFillMode();
              parameter = cpResult;
              altCompare = true;
              break;
            case enumUpdateProperty.cellFontColor:
              var cpResult = new QColorPickerResult();
              cpResult.fSelectedColor = cellDesc.GetFontColor();
              parameter = cpResult;
              altCompare = true;
              break;
            case enumUpdateProperty.cellTextAlign:
              parameter = cellDesc.GetAlignment();
              break;
            case enumUpdateProperty.cellTextVAlign:
              parameter = cellDesc.GetVAlignment(true);
              break;
            case enumUpdateProperty.cellWrapText:
              parameter = cellDesc.GetWrapText();
              break;
            case enumUpdateProperty.picFrame:
              parameter = cellDesc.GetPictureFrame();
              altCompare2 = true;
              break;
            case enumUpdateProperty.picMask:
              parameter = cellDesc.GetPictureMask();
              break;
            case enumUpdateProperty.fieldDisplayMode:
              parameter = cellDesc.GetFieldDisplayMode();
              break;
            case enumUpdateProperty.fontSize:
              parameter = cellDesc.GetFontSize();
              break;
            case enumUpdateProperty.fontName:
              parameter = cellDesc.GetFontName();
              break;
          }

          if (prevParameter != null) {
            if (altCompare) {
              if (!CPCompare(prevParameter, parameter)) return null;
            } else if (altCompare2) {
              if (!prevParameter.Compare(parameter)) return null;
            } else if (prevParameter != parameter) return null;
          }
          prevParameter = parameter;
        }
      }
    }

    return parameter;
  }

  PrepRender(theWSMgr) {}

  AssembleCommand(theWSMgr) {}

  DoColumnResize(knobIndex, knobValue) {
    var theLayout = this.fLayout;

    var columnIndex = knobIndex + 1;
    var curColSum = theLayout.SumColWidths(columnIndex);
    var delta = knobValue - curColSum;

    if (delta != 0) {
      theLayout.ResizeColumn(columnIndex, delta, false);

      //this.FlagContentChanged();
      this.BuildLayout(theLayout, true);

      this.PositionMinMaxControls();
      this.fWSMgr?.OnLayoutMutated?.();
    }
  }

  PanelHandleKeyboard(keyInfo, targetElement, wsMgr) {
    var keyCode = keyInfo.keyCode;
    var handled = false;

    if (keyInfo.ctrlKey) keyCode = wsMgr.ConvertCtrlKeysToCmds(keyInfo);

    switch (keyCode) {
      case kReturnKey:
        if (targetElement.InEditMode()) {
          handled = targetElement.ExitEditMode();
          break;
        }
        break;
      case "Redo":
      case "Undo":
        // we do not want the wsmgr handling Undo while the layout panel is open.
        // Undo will be redirected to the main canvas panel in that case...
        handled = true;
        break;
      default:
        break;
    }
    return handled;
  }

  ExecuteCommand(aCmd) {
    var cmdCode = aCmd.GetCmdCode();
    if (cmdCode != null) {
      var cmdProcessor = cmdCode.split("-");
      var srcElement = aCmd.GetSourceElement();
      if (cmdProcessor.length > 1 && cmdProcessor != cmdCode) {
        cmdCode = cmdProcessor.shift();
        var subCode = cmdProcessor.join("-");
      }
    }
    var handled = false;

    switch (cmdCode) {
      case "ChgMinMax":
        var theLayout = this.GetLayout();
        var knobIndex = aCmd["KnobIndex"];
        if (knobIndex != null) {
          var knobValue = aCmd["KnobValue"];
          if (knobIndex == 0) theLayout.SetWidthMin(knobValue);
          else theLayout.SetWidthMax(knobValue);
          this.BuildLayout(this.fLayout, true);
          this.fWSMgr?.OnLayoutMutated?.();
        }
        //else
        //	console.log("No info");
        handled = true;
        break;
      case "ResizeColumn":
        var knobIndex = aCmd["KnobIndex"];
        var knobValue = aCmd["KnobValue"];

        this.DoColumnResize(knobIndex, knobValue);
        // console.log(knobIndex, " ", knobValue);
        handled = true;
        break;
      case "SelectAggregatedField":
        var objDB = this.fWSMgr.GetDoc().GetObjDB();
        var theClass = objDB.GetClassByName("Box");
        var fieldList = theClass.GetFieldDefListByType(enumCalcCode.aggregatedField);

        var aggregatedFieldOptions = new QOptionArray();
        for (var i = 0; i < fieldList.length; i++) {
          var aField = fieldList[i];
          var displayName = aField.GetFieldName();
          var fieldMetadata = aField.GetTableRef();
          var disabledField =
            fieldMetadata == null ||
            (fieldMetadata["rowAggregationResult"] == null &&
              fieldMetadata["columnAggregationResult"] == null); // field is missing core metadata
          var hoverCode = null;
          if (disabledField) {
            displayName += "*";
            hoverCode = "Refresh the source chart prior to adding this field to a table.";
          }
          var theOpt = aggregatedFieldOptions.addOption(
            "setAggregatedField",
            false,
            displayName,
            null,
            disabledField,
            null,
            null,
            null,
            hoverCode
          );
          theOpt.fAdditionalCmdParameters = { fieldRef: aField };
        }

        var valuePicker = new QDropDown();
        valuePicker.InitDropDown(
          this.fWSMgr,
          this,
          null,
          aggregatedFieldOptions,
          0,
          true,
          aCmd.GetSourceElement()
        );
        break;
      case "setAggregatedField":
        var myBox = this.fChartBox;
        var layout = myBox.GetLayout();
        layout.SetAggregatedFieldRef(aCmd["fieldRef"]);
        layout.fDeferredTableLoad = true;
        // don't want to do this for pie charts...
        if (myBox.GetBoxSubType() === enumBoxSubTypes.enumeratedTable) {
          layout.GenerateEnumeratedTableLayout();
        }
        this.BuildLayout(this.fLayout, true);
        this.fWSMgr?.OnLayoutMutated?.();
        break;
      case "changeField":
        var targetCell = aCmd.GetSourcePanel().fFieldRef;
        if (srcElement.fName === "Free Text") {
          // prolly more elegant way to do this
          targetCell.SetField(null);
          targetCell.SetCellContentType(enumCellContentType.FreeText);
          targetCell.SetCellMode(enumChartBoxInteractionMode.layout);
          this.fWSMgr.StartEditCellContent(this, targetCell);
        } else {
          targetCell.SetField(srcElement.fFieldDef);
        }
        this.fWSMgr?.OnLayoutMutated?.();
        handled = true;
        break;
    }

    return handled;
  }

  StartFieldDrag(sourceFieldElement, canvasX, canvasY) {
    this.ClearSelection();
    this.DoSelectElement(sourceFieldElement, true);

    var sw = sourceFieldElement.GetWidth();
    var sh = kDefaultCellHeight;

    var wCenter = Math.floor(sh / 2);

    var aFieldDef = sourceFieldElement.GetField();
    var isFreeText = sourceFieldElement.IsFreeText();
    if (aFieldDef || isFreeText) {
      var imageMgr = this.fWSMgr.GetImageMgr();

      this.fDraggerPanel = new QLayoutDragPanel();
      this.fDraggerPanel.InitFieldDraggerPanel(
        aFieldDef,
        isFreeText,
        sourceFieldElement,
        canvasX - 6,
        canvasY - wCenter,
        sw,
        sh,
        this,
        this,
        "LayoutFieldDragComplete",
        imageMgr
      );

      this.fWSMgr.PushPanel(this.fDraggerPanel);
      this.fDraggerPanel.SetDragStart(canvasX, canvasY);
      this.fWSMgr.SetTarget(this.fDraggerPanel, this.fDraggerPanel.GetFieldElement());
      this.fDraggerPanel.SetShownState(false);
    }
  }

  RebuildLayout() {
    this.BuildLayout(this.fLayout, true);
  }

  DragComplete() {
    this.fDraggerPanel.DragComplete();
    this.fWSMgr.RemovePanel(this.fDraggerPanel);
    this.fDraggerPanel = null;
  }

  SupportsZoom() {
    return true;
  }

  HandleRightClickCmd(cmdCode, aCmd) {
    var param = null;

    switch (cmdCode) {
      case "scopechart":
        param = enumCellRefType.topbox;
        break;
      case "scopebranch":
        param = enumCellRefType.branch;
        break;
    }

    if (param != null) this.UpdateSelectedProperty(param, enumUpdateProperty.cellRefType);
  }

  GetSelectedCellDescs() {
    var chartBox = this.fChartBox;
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetSelectedCellDescs(boxCells);
  }

  DoMergeCells() {
    this.ApplyMergeCells();
    this.BuildLayout(this.fLayout, true);
    this.fWSMgr?.OnLayoutMutated?.();
  }

  DoUnmergeCells() {
    this.ApplyUnMergeCells();
    this.BuildLayout(this.fLayout, true);
    this.fWSMgr?.OnLayoutMutated?.();
  }

  AddDivider(x, y, panel) {
    var aDivider = new QDivider();
    aDivider.PositionElement(x, y - 6);
    aDivider.InitDivider(enumDividerDirection.vertDivider, 8.5, false);
    aDivider.SizeElement(8, panel.fHeight);
    panel.PushElement(aDivider);
    return aDivider;
  }

  IsAggObj() {
    var boxType = this.fLayout.GetBoxSubType();
    var isAggField = this.fLayout.GetAggregatedFieldRef() != null;
    var obj = boxType === enumBoxSubTypes.enumeratedTable || boxType === enumBoxSubTypes.refChart;
    return obj && isAggField;
  }

  Query(queryCode) {
    switch (queryCode) {
      case "FirstSelBox":
        return this.fFirstSelBox;
      case "FirstSelCell":
        return this.fFirstSelCell;
      case "FirstSelGutter":
        return this.fFirstSelGutter;
      default:
        break;
    }
    return null;
  }

  SubPrepRender(wsMgr) {
    this.fFirstSelBox = wsMgr.GetFirstSelectedBox(true);
    this.fFirstSelCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
    this.fFirstSelGutter = wsMgr.GetFirstSelectedElement(enumChartElement.gutterCell);
  }
}
